// Back-in-Stock notify — brand-styled, self-hosted capture form.
//
// Posts directly to Klaviyo's PUBLIC Client Back-in-Stock endpoint (no auth;
// the company id is the public site id). Variant-aware: the gold trigger button
// replaces the dead "SOLD OUT" button only when the selected variant is
// unavailable, and the submission carries that exact variant id so a shopper
// waiting on one size/variant is notified for the right one.
//
// Klaviyo catalog-variant id format for Shopify: $shopify:::$default:::<numeric variant id>
// Subscribing records the "Subscribed to Back in Stock" metric, which triggers
// the Back in Stock flow built in Klaviyo.

const KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

class NotifyBackInStock extends HTMLElement {
  connectedCallback() {
    this.companyId = this.dataset.companyId;
    this.revision = this.dataset.revision || '2026-04-15';
    this.currentVariantId = this.dataset.currentVariant || '';

    this.trigger = this.querySelector('[data-bis-trigger]');
    this.dialog = this.querySelector('[data-bis-dialog]');
    this.emailInput = this.querySelector('[data-bis-email]');
    this.errorEl = this.querySelector('[data-bis-error]');
    this.submitBtn = this.querySelector('[data-bis-submit]');
    this.buttons = this.closest('.product-form-buttons');

    this.trigger?.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-bis-close]').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    // backdrop click closes
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    // Not a <form> (would nest inside Shopify's product form) — submit on
    // button click + Enter in the email field.
    this.submitBtn?.addEventListener('click', (event) => this.submit(event));
    this.emailInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submit(event);
      }
    });

    this.section = this.closest('.shopify-section, dialog') || document;
    this.section.addEventListener('variant:update', this.onVariantUpdate);
  }

  disconnectedCallback() {
    this.section?.removeEventListener('variant:update', this.onVariantUpdate);
  }

  onVariantUpdate = (event) => {
    const resource = event.detail?.resource;
    if (!resource) return;
    if (resource.id != null) this.currentVariantId = String(resource.id);
    // resource.available === false means this variant is sold out
    this.setSoldout(resource.available === false);
    this.showView('form'); // reset state when the shopper changes variant
  };

  setSoldout(isSoldout) {
    this.buttons?.classList.toggle('is-soldout', isSoldout);
  }

  open() {
    this.showView('form');
    this.setError('');
    if (typeof this.dialog?.showModal === 'function') this.dialog.showModal();
    else this.dialog?.setAttribute('open', '');
    requestAnimationFrame(() => this.emailInput?.focus());
  }

  close() {
    if (typeof this.dialog?.close === 'function') this.dialog.close();
    else this.dialog?.removeAttribute('open');
  }

  showView(name) {
    this.querySelectorAll('[data-bis-view]').forEach((view) => {
      view.toggleAttribute('hidden', view.dataset.bisView !== name);
    });
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.toggleAttribute('hidden', !message);
    if (message) this.emailInput?.setAttribute('aria-invalid', 'true');
    else this.emailInput?.removeAttribute('aria-invalid');
  }

  async submit(event) {
    event.preventDefault();
    const email = (this.emailInput?.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      this.setError('Please enter a valid email address.');
      this.emailInput?.focus();
      return;
    }
    if (!this.currentVariantId) {
      this.setError('Something went wrong. Please refresh and try again.');
      return;
    }
    this.setError('');
    this.setLoading(true);

    try {
      const response = await fetch(
        `${KLAVIYO_BIS_ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: this.revision },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${this.currentVariantId}`,
                  },
                },
              },
            },
          }),
        }
      );

      if (response.status === 202 || response.ok) {
        this.showView('success');
      } else {
        const detail = await response.text().catch(() => '');
        console.error('[notify-back-in-stock] Klaviyo error', response.status, detail);
        this.setError('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('[notify-back-in-stock] network error', error);
      this.setError('Network error. Please check your connection and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = isLoading;
    this.submitBtn.toggleAttribute('data-loading', isLoading);
  }
}

if (!customElements.get('notify-back-in-stock')) {
  customElements.define('notify-back-in-stock', NotifyBackInStock);
}
